import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";

import { Table } from "src/core/dashboard/components/widgets/index";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { getOrderTableColumns } from "src/core/shared/components/tables/ordersTableColumns";
import {
  ListOrdersByDropParams,
  ordersApi,
  OrdersResponse,
  IOrder
} from "src/core/orders/api/orders.api";
import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import { setOrdersWithErrors } from "src/core/orders/store/orders.actions";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import { useDrop } from "src/core/drops/hooks";
import { useCurrentUser } from "src/core/users/hooks";

export type OrdersViewType = "calendar" | "table";

const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

const ListOrdersPage: React.ReactFCWithChildren<PageProps<unknown, {listId: string;}>> = props => {
  const dispatch = useDispatch();
  const TableRef = React.useRef<TableRefProps>(null);

  const {
    history, terminology, match
  } = props;

  const listId = React.useMemo(() => match?.params.listId ? parseInt(match.params.listId, 0) : 0, [ match?.params.listId ]);
  const [ { dropIdMemo } ] = useDrop();
  const [ { currentRole } ] = useCurrentUser();

  const {
    orderStatuses, role, erp, installedModules
  } = useMappedState((store: IStore) => ({
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || []
  }));

  const [ ordersResponse, getOrders ] = ordersApi<OrdersResponse>("LIST_DROP_ORDERS");
  const ordersData = React.useMemo(() => ordersResponse.data?.orders.result || [], [ ordersResponse.data?.orders.result ]);
  const pricesVisible = usePriceVisibility();

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<IOrder>, pagination: PaginatedParams<IOrder>}) => {
      const { orderStatus, ...filters } = params.filters;

      if (dropIdMemo && listId) {
        getOrders<ListOrdersByDropParams>({
          pagination: params.pagination,
          filters: {
            ...filters,
            orderStatusId: orderStatus?.length ? parseInt(orderStatus, 10) : undefined,
            listId
          },
          dropId: dropIdMemo
        });
      }
    },
    [ dropIdMemo, listId ]
  );

  const viewOrder = (id: number) => {
    history.push(`/orders/${id}`);
  };

  React.useEffect(() => {
    if (ordersResponse.data) {
      dispatch(setOrdersWithErrors(ordersResponse.data.allOrdersWithErrors));
    }
  }, [ ordersResponse, dispatch ]);

  const reload = React.useCallback(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ TableRef ]);

  const tableColumns = React.useMemo(() => {
    if (!dropIdMemo || !erp || !installedModules || !orderStatuses || !role || !terminology) {
      return [];
    }

    return getOrderTableColumns({
      installedModules,
      terminology,
      orderStatuses,
      pricesVisible,
      actionButton: info => {
        if (dropIdMemo && erp && role) {
          return (
            <OrderActionsList
              erp={erp}
              terminology={terminology}
              role={role}
              order={info.row.original}
              reload={reload}
            />
          );
        }

        return null;
      },
      hideDropCustomer: currentRole?.code !== "customer-admin"
    });
  }, [
    dropIdMemo,
    erp,
    installedModules,
    orderStatuses,
    role,
    terminology,
    pricesVisible,
    currentRole?.code,
    reload
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Table
          tableOf={`${terminology.list.singular.text} ${terminology.order.plural.text}`}
          className="large"
          data={ordersData}
          columns={tableColumns}
          onFetchData={onFetchData}
          defaultPagination={defaultPagination}
          loading={ordersResponse.pending}
          pageCount={ordersResponse.data?.orders.pages}
          rowCount={ordersResponse.data?.orders.count}
          pageSizeOptions={[
            10,
            20,
            50,
            100
          ]}
          ref={TableRef}
          mainAction={row => viewOrder(row.original.id)}
        />

      </MainContentWrapper>
    </Layout>
  );
};

export default ListOrdersPage;
